/**
 * InputRut — input único de RUT chileno para el superadmin (H17).
 *
 * Réplica del componente del portal (apps Vite separadas sin paquete
 * compartido). Encapsula formato en vivo + validación mod11 + salida canónica
 * + error inline, para que ningún formulario olvide validar. Doctrina RUT
 * H1-H4: storage canónico, display con puntos. El backend revalida.
 */
import { useState } from 'react';
import { formatearRutEnVivo, validarRutChileno } from '../../lib/format';

export interface RutValidez {
  valid: boolean;
  canonico: string | null;
}

interface InputRutProps {
  value: string;
  onChange: (formateado: string, validez: RutValidez) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Si false, vacío NO muestra error (RUT opcional). Default true. */
  requerido?: boolean;
  className?: string;
}

const CLASE_BASE =
  'w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 ' +
  'text-text-primary px-3 py-2 text-sm';

export function InputRut({
  value,
  onChange,
  id,
  placeholder = '12.345.678-9',
  disabled = false,
  requerido = true,
  className = '',
}: InputRutProps) {
  const [tocado, setTocado] = useState(false);
  const vacio = value.trim() === '';
  const validez = validarRutChileno(value);
  const mostrarError = tocado && !validez.valid && (!vacio || requerido);

  return (
    <>
      <input
        type="text"
        // `text` y NO `numeric`: el dígito verificador del RUT puede ser "K",
        // y el teclado numérico de móvil no ofrece letras → no se podían
        // ingresar RUTs terminados en K (bug reportado 2026-07-06).
        inputMode="text"
        id={id}
        value={value}
        onChange={(e) => {
          const f = formatearRutEnVivo(e.target.value);
          onChange(f, validarRutChileno(f));
        }}
        onBlur={() => setTocado(true)}
        maxLength={12}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={mostrarError || undefined}
        className={`${CLASE_BASE} ${className}`.trim()}
      />
      {mostrarError && (
        <p className="text-xs text-state-danger mt-1" role="alert">
          {vacio
            ? 'El RUT es obligatorio.'
            : 'RUT inválido — revisa el dígito verificador.'}
        </p>
      )}
    </>
  );
}
